import React, { useEffect, useMemo, useState } from "react";
import { AttendanceRecord } from "../../models/attendance.model";
import {
  fetchAttendanceMetadata,
  fetchMonthlyReport,
} from "../../store/slices/attendanceSlice";
import { RootDispatch, RootState } from "../../store";
import { CheckCircle, ChevronDown, Clock, MapPin, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  formatLocation,
  formatTime,
  getStatusIcon,
} from "../../utils/attendanceUtils";
import { fetchEmployeeDetails } from "../../store/slices/employeeSlice";
import { isWeekend as checkWeekend } from "../../utils/attendanceUtils";
import {
  createOrUpdateManualOverride,
  fetchUserManualOverrides,
} from "../../store/slices/regularizeSlice";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface CalendarProps {
  year: number;
  month: number; // 1-12
  monthlyMap: Record<string, AttendanceRecord>;
  setSelectedMonth: (month: number) => void;
  selectedMonth: number;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  selectedUser: string | null;
  isModalOpen: boolean;
  onClose: () => void;
}

const AttendanceCalendar: React.FC<CalendarProps> = ({
  year,
  month,
  monthlyMap,
  setSelectedMonth,
  setSelectedYear,
  selectedMonth,
  selectedYear,
  selectedUser,
  isModalOpen,
  onClose,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(
    null
  );
  const { role } = useSelector((state: RootState) => state?.auth);

  const holidays = useSelector((state: RootState) => state.attendance.holidays);
  const employee = useSelector(
    (state: RootState) => state.employee.userDetails
  );
  const weekendPolicy = employee?.weekendPolicy || "general_sun_off_only";

  useEffect(() => {
    dispatch(fetchAttendanceMetadata());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUser) {
      dispatch(fetchEmployeeDetails(selectedUser));
    }
  }, [dispatch]);

  const holidayMap = useMemo(() => {
    const map: Record<string, string> = {};
    (holidays || []).forEach((h) => {
      const key = new Date(h.date).toISOString().slice(0, 10);
      map[key] = h.name;
    });
    return map;
  }, [holidays]);

  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];

  const monthIndex = month - 1;
  const firstDay = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  // 2D array: weeks[weekIndex][0..6] = day number or null if blank
  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = Array(firstDay.getDay()).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    // fill trailing blanks
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  if (!isModalOpen || !selectedUser) {
    return null;
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const m = parseInt(e.target.value, 10);
    setSelectedMonth(m);
    if (selectedUser) {
      dispatch(
        fetchMonthlyReport({
          month: m,
          year: year,
          userId: selectedUser,
        })
      );
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const y = parseInt(e.target.value, 10);
    setSelectedYear(y);
    if (selectedUser) {
      dispatch(
        fetchMonthlyReport({
          month: month,
          year: y,
          userId: selectedUser,
        })
      );
    }
  };

  const generateYearOptions = () => {
    const years: number[] = [];
    const currentYear = today.getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      years.push(y);
    }
    return years;
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const totalPresentCount = Object.values(monthlyMap).filter(
    (rec) =>
      rec.firstSessionStatus?.toLowerCase() === "present" ||
      rec.secondSessionStatus?.toLowerCase() === "present"
  ).length;

  const statusColors: Record<string, { bg: string; text: string }> = {
    present: { bg: "bg-green-100", text: "text-green-700" },
    absent: { bg: "bg-red-100", text: "text-red-700" },
    "paid leave": { bg: "bg-yellow-100", text: "text-yellow-700" },
    "sick leave": { bg: "bg-blue-100", text: "text-blue-700" },
    "casual leave": { bg: "bg-purple-100", text: "text-purple-700" },
    compoff: { bg: "bg-pink-100", text: "text-pink-700" },
    "loss of pay": { bg: "bg-gray-100", text: "text-gray-700" },
    holiday: { bg: "bg-orange-100", text: "text-orange-700" },
    weekend: { bg: "bg-sky-100", text: "text-sky-700" },
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 transition-opacity overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="inline-block align-bottom bg-white rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-4 sm:mb-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Monthly Attendance Report
                  </h3>
                  <p className="text-xs text-gray-600">
                    View detailed attendance data for selected period
                  </p>
                </div>

                {/* Date Selectors */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <select
                      value={selectedMonth}
                      onChange={handleMonthChange}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-xs font-medium text-gray-700 hover:border-navyBlue-primary focus:outline-none focus:ring-2 focus:ring-navyBlue-primary focus:border-transparent transition-all duration-200 shadow-xs"
                    >
                      {monthNames.map((name, idx) => (
                        <option key={idx} value={idx + 1}>
                          {name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>

                  <div className="relative">
                    <select
                      value={selectedYear}
                      onChange={handleYearChange}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-xs font-medium text-gray-700 hover:border-navyBlue-primary  focus:outline-none focus:ring-2 focus:ring-navyBlue-primary focus:border-transparent transition-all duration-200 shadow-sm"
                    >
                      {generateYearOptions().map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="flex items-cente text-gray-700 text-xs font-medium px-3 py-2 ">
                    <CheckCircle className="w-4 h-4 mr-1 " />
                    <span>Total Present: {totalPresentCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar */}
            <div className="p-4">
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-px mb-px bg-gray-200 rounded-t-lg overflow-hidden">
                    {dayNames.map((dayName, index) => (
                      <div
                        key={dayName}
                        className={`p-3 text-center text-xs font-semibold text-gray-700 bg-gray-50 ${
                          index === 0 || index === 6 ? "text-blue-600" : ""
                        }`}
                      >
                        {dayName}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-b-lg overflow-hidden">
                    {weeks.map((week, weekIndex) =>
                      week.map((day, dayIndex) => {
                        if (day === null) {
                          return (
                            <div
                              key={`${weekIndex}-${dayIndex}`}
                              className="h-32 bg-gray-50"
                            />
                          );
                        }

                        const dateKey = `${year}-${String(month).padStart(
                          2,
                          "0"
                        )}-${String(day).padStart(2, "0")}`;
                        const rec = monthlyMap[dateKey];
                        const isToday = dateKey === currentDate;
                        const dateObj = new Date(
                          selectedYear,
                          selectedMonth - 1,
                          day
                        );
                        const isWeekend = checkWeekend(dateObj, weekendPolicy);
                        const isHoliday = !!holidayMap[dateKey];

                        return (
                          <div
                            key={`${weekIndex}-${dayIndex}`}
                            className={`h-26 bg-white p-2 relative overflow-hidden hover:bg-gray-50 transition-colors duration-200 ${
                              isToday
                                ? "ring-2 ring-navyBlue-primary ring-inset"
                                : ""
                            }`}
                            onClick={() => {
                              if (isWeekend || isHoliday) return;

                              setSelectedRecord(
                                rec || {
                                  date: dateKey,
                                  firstSessionStatus: "Absent",
                                  secondSessionStatus: "Absent",
                                  userId: selectedUser,
                                  remarks: "",
                                }
                              );
                              setOverrideModalOpen(true);
                            }}
                          >
                            {/* Day Number */}
                            <div
                              className={`text-xs font-semibold mb-1 ${
                                isToday
                                  ? "text-blue-600"
                                  : isWeekend
                                  ? "text-gray-500"
                                  : "text-gray-900"
                              }`}
                            >
                              {day}
                              {isToday && (
                                <div className="absolute top-1 right-1 w-2 h-2 bg-navyBlue-primary rounded-full"></div>
                              )}
                            </div>

                            {/* Attendance Data */}
                            {rec && rec.punchIn ? (
                              <div className="space-y-1">
                                {/* First Session */}
                                {rec.firstSessionStatus && (
                                  <div
                                    className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${
                                      statusColors[
                                        rec.firstSessionStatus.toLowerCase()
                                      ]?.bg
                                    } ${
                                      statusColors[
                                        rec.firstSessionStatus.toLowerCase()
                                      ]?.text
                                    }`}
                                  >
                                    {" "}
                                    {getStatusIcon(rec.firstSessionStatus)}
                                    <span className="capitalize">
                                      1st: {rec.firstSessionStatus}
                                    </span>
                                  </div>
                                )}

                                {/* Second Session */}
                                {rec.secondSessionStatus && (
                                  <div
                                    className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${
                                      statusColors[
                                        rec.secondSessionStatus.toLowerCase()
                                      ]?.bg
                                    } ${
                                      statusColors[
                                        rec.secondSessionStatus.toLowerCase()
                                      ]?.text
                                    }`}
                                  >
                                    {" "}
                                    {getStatusIcon(rec.secondSessionStatus)}
                                    <span className="capitalize">
                                      2nd: {rec.secondSessionStatus}
                                    </span>
                                  </div>
                                )}

                                {/* Punch In/Out */}
                                <div className="space-y-0.5 text-xs">
                                  <div className="flex items-center gap-1 text-green-teal">
                                    <Clock className="w-3 h-3" />
                                    <span className="font-medium">In:</span>
                                    <span>{formatTime(rec.punchIn)}</span>
                                  </div>

                                  <div className="flex items-center gap-1 text-red-secondary">
                                    <Clock className="w-3 h-3" />
                                    <span className="font-medium">Out:</span>
                                    <span>
                                      {rec.punchOut
                                        ? formatTime(rec.punchOut)
                                        : ""}
                                    </span>
                                  </div>
                                </div>

                                {/* Location Info show on hover */}
                                <div className="absolute inset-x-0 bottom-0 bg-white border-t border-gray-100 px-2 py-1 opacity-0 hover:opacity-100 transition-opacity duration-200 text-xs">
                                  {rec.punchInLocation && (
                                    <div className="flex items-center gap-1 text-gray-500 mb-0.5">
                                      <MapPin className="w-3 h-3" />
                                      <span className="truncate">
                                        {formatLocation(rec.punchInLocation)}
                                      </span>
                                    </div>
                                  )}
                                  {rec.punchOutLocation && (
                                    <div className="flex items-center gap-1 text-gray-500">
                                      <MapPin className="w-3 h-3" />
                                      <span className="truncate">
                                        {formatLocation(rec.punchOutLocation)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-16">
                                {isHoliday ? (
                                  <div
                                    className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${statusColors["holiday"].bg} ${statusColors["holiday"].text}`}
                                  >
                                    {holidayMap[dateKey]}
                                  </div>
                                ) : isWeekend ? (
                                  <div
                                    className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${statusColors["weekend"].bg} ${statusColors["weekend"].text}`}
                                  >
                                    Weekend
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium">
                                    No record
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {overrideModalOpen && selectedRecord && (role === "admin" || role === "hr" ) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">
              Override Attendance for {selectedRecord.date}
            </h2>

            {/* First Session */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Session Status
              </label>
              <select
                value={selectedRecord.firstSessionStatus || "Absent"}
                onChange={(e) =>
                  setSelectedRecord({
                    ...selectedRecord,
                    firstSessionStatus: e.target.value,
                  })
                }
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
              </select>
            </div>

            {/* Second Session */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Second Session Status
              </label>
              <select
                value={selectedRecord.secondSessionStatus || "Absent"}
                onChange={(e) =>
                  setSelectedRecord({
                    ...selectedRecord,
                    secondSessionStatus: e.target.value,
                  })
                }
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
              </select>
            </div>
            {/* Remark */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark
              </label>
              <textarea
                value={selectedRecord.remarks || ""}
                onChange={(e) =>
                  setSelectedRecord({
                    ...selectedRecord,
                    remarks: e.target.value,
                  })
                }
                className="w-full border rounded-lg px-3 py-2 text-sm resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add a remark or note..."
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setOverrideModalOpen(false)}
                className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedRecord) return;

                  // Cast to strict union type
                  const payload = {
                    userId: selectedUser!,
                    date: selectedRecord.date,
                    session1Status: selectedRecord.firstSessionStatus as
                      | "Present"
                      | "Absent",
                    session2Status: selectedRecord.secondSessionStatus as
                      | "Present"
                      | "Absent",
                    remarks: selectedRecord.remarks || "",
                  };
                  try {
                    await dispatch(
                      createOrUpdateManualOverride(payload)
                    ).unwrap();
                    dispatch(
                      fetchMonthlyReport({
                        month: selectedMonth,
                        year: selectedYear,
                        userId: selectedUser,
                      })
                    );
                    setOverrideModalOpen(false);
                  } catch (err) {
                    console.error("Failed to save override:", err);
                  }
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceCalendar;
